import React from 'react';
import {
  Box,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
  TableHeaderCell,
  Text,
  Table,
  TableHeaderRow,
  Button,
  Amount,
  Spinner,
} from '@razorpay/blade/components';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import { useModal } from 'components/ui/Modals';
import { ArrearBreakdownV2 } from './ArrearBreakdownV2';
import { DeferArrearsCommit, DeferredArrearsRow } from './types';
import { queryKeys } from './data';
import { useQuery } from 'react-query';
import api from 'api';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { RunPayrollData } from 'components/RunPayroll/utils/types';
import { getCombinedArrears, getComponentName } from './utils';
import { generateOrgId, generateUserId } from 'utils/commons';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { format } from 'date-fns';
import { dateFormats } from 'utils/Dates';

const ArrearsV2 = ({
  payrollEntry,
  payrollMonth,
  onDeferCommit,
  pendingRows,
}: {
  payrollEntry: RunPayrollData;
  payrollMonth: string;
  onDeferCommit?: DeferArrearsCommit;
  pendingRows?: DeferredArrearsRow[];
}) => {
  const orgId = generateOrgId(useReduxSelector(loggedInUserSelectors.organizationID) ?? '');
  const userId = generateUserId(payrollEntry.peopleId ?? '');

  const {
    data: combinedArrearsTableData,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useQuery({
    queryFn: () =>
      api.arrears.queries.fetchArrears({
        merchantId: orgId,
        tenantId: orgId,
        filterCriteria: {
          userIds: [userId],
          targetMonth: format(new Date(payrollMonth), dateFormats.yearMonth),
        },
        includeComponents: true,
      }),
    queryKey: [queryKeys.FETCH_ARREARS],
    enabled: !!orgId && !!userId,
    select: (data) => getCombinedArrears(data),
  });

  const { openModal } = useModal();

  const onAddEditArrears = () => {
    openModal(
      <ArrearBreakdownV2
        payrollEntry={payrollEntry}
        payrollMonth={payrollMonth}
        onDeferCommit={onDeferCommit}
      />,
    );
  };

  const hasPendingRows = pendingRows !== undefined && pendingRows !== null;
  const arrearsTableData = hasPendingRows ? pendingRows : combinedArrearsTableData;

  const hasNoArrears = !isLoading && !isFetching && !error && !arrearsTableData?.length;

  if (hasNoArrears) {
    return (
      <ResetLocalOverridingStyles key="empty-arrears-list">
        <EmptyListView
          size="small"
          title=""
          description="No Arrears Added"
          paddingTop="spacing.4"
          paddingBottom="spacing.4">
          <Box display="flex" justifyContent="center" alignItems="center" gap="spacing.4">
            <Button size="xsmall" onClick={onAddEditArrears}>
              Add Arrears
            </Button>
            {/* <Button size='small' variant='tertiary'>Add RLOP Days</Button> */}
          </Box>
        </EmptyListView>
      </ResetLocalOverridingStyles>
    );
  }

  if (error) {
    return (
      <ResetLocalOverridingStyles key="error-arrears-list">
        <ErrorView
          onRefetch={refetch}
          backgroundColor="transparent"
          borderWidth={'none'}
          height="100%"
        />
      </ResetLocalOverridingStyles>
    );
  }

  if (isLoading || isFetching) {
    return (
      <ResetLocalOverridingStyles key="loading-arrears-list">
        <Box display="flex" justifyContent="center" alignItems="center">
          <Spinner accessibilityLabel="Loading arrears" />
        </Box>
      </ResetLocalOverridingStyles>
    );
  }

  return (
    <ResetLocalOverridingStyles key={`arrears-list-${arrearsTableData?.length ?? 0}`}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="spacing.5"
        marginBottom="spacing.4">
        <Text color="surface.text.gray.muted">
          {hasPendingRows
            ? 'Arrears below apply once this edit is approved'
            : 'Below table contain arrears'}
        </Text>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="spacing.4">
          <Button variant="secondary" size="xsmall" onClick={onAddEditArrears}>
            Edit Arrears
          </Button>
          {/* <Button variant="tertiary" size="xsmall">
            Add RLOP days
          </Button> */}
        </Box>
      </Box>
      <Box>
        <Table data={{ nodes: arrearsTableData || [] }}>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Component</TableHeaderCell>
                  <TableHeaderCell textAlign="right">Amount</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>

              <TableBody>
                {tableData.map((row) => (
                  <TableRow key={row.id} item={row}>
                    <TableCell>
                      <Text>{getComponentName({ name: row.component, sdiType: row.sdiType })}</Text>
                    </TableCell>
                    <TableCell textAlign="right">
                      <Amount value={row.arrearAmount} currency="INR" suffix="none" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </Box>
    </ResetLocalOverridingStyles>
  );
};

export default ArrearsV2;
